import React from 'react'
import PropTypes from 'prop-types'
import { Button, Dropdown, Form, Icon } from 'semantic-ui-react'

/**
 * Reusable card for a single category in the Add Categories section.
 * Displays a title, item count, an "Add" button, and one or more dropdowns
 * for selecting/editing existing items.
 *
 * Props:
 *   title              {string}    Display title for the category
 *   options            {Array}     Dropdown options for existing items
 *   placeholder        {string}    Placeholder text for the main dropdown
 *   value              {string}    Current selected value in the dropdown
 *   name               {string}    Name attribute for the dropdown
 *   onSelectItem       {Function}  Called when a dropdown item is selected (edit). Signature: fn(e, data)
 *   onAdd              {Function}  Called when "Add" button is clicked. Signature: fn()
 *   parentDropdown     {Object}    [optional] Config for a parent dropdown (e.g. Degree selection before University)
 *   showChildDropdown  {boolean}   [optional] Whether to show the main dropdown (used when parent selection is required)
 *   childLabel         {string}    [optional] Label for the main dropdown when a parent is present
 */
const CategoryCard = ({
  title,
  options,
  placeholder,
  value,
  name,
  onSelectItem,
  onAdd,
  parentDropdown,
  showChildDropdown,
  childLabel,
}) => {
  const itemCount = options ? options.length : 0
  const hasParent = Boolean(parentDropdown)

  return (
    <div className="add-cat-card">
      <div className="add-cat-card-header">
        <h4 className="add-cat-card-title">
          {title}
          <span className="add-cat-card-count">({itemCount})</span>
        </h4>
        <Button
          className="add-cat-card-add-btn"
          size="mini"
          onClick={onAdd}
          aria-label={'Add ' + title}
        >
          <Icon name="plus" />
          Add
        </Button>
      </div>

      <div className="add-cat-card-body">
        <Form>
          {hasParent && (
            <Form.Field>
              <Dropdown
                placeholder={parentDropdown.placeholder}
                search
                selection
                multiple={false}
                options={parentDropdown.options || []}
                onChange={parentDropdown.onChange}
                name={parentDropdown.name}
                value={parentDropdown.value}
              />
            </Form.Field>
          )}

          {(!hasParent || showChildDropdown) && (
            <Form.Field>
              <Dropdown
                placeholder={placeholder}
                search
                selection
                multiple={false}
                options={options || []}
                onChange={onSelectItem}
                value={value}
                name={name}
              />
            </Form.Field>
          )}
        </Form>
      </div>
    </div>
  )
}

CategoryCard.propTypes = {
  title: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string.isRequired,
  onSelectItem: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  parentDropdown: PropTypes.shape({
    label: PropTypes.string,
    placeholder: PropTypes.string,
    options: PropTypes.array,
    onChange: PropTypes.func,
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  showChildDropdown: PropTypes.bool,
  childLabel: PropTypes.string,
}

export default CategoryCard
